/**
 * The three cross-conversation views — V7-04 §2 (Threads), §5 (Saved), §6
 * (Search).
 *
 * They sit together because they are the same component with different rows:
 * a list of things found elsewhere in chat, each of which jumps you to where it
 * lives. That "jump to context" is the part that matters — a saved message you
 * cannot get back to is a bookmark with no page.
 *
 * §7 asks for an empty state on every new view, and each one here says what to
 * do rather than that there is nothing: "nothing found" is a dead end, "reply
 * to a message to start a thread" is an instruction.
 */

import { Bookmark, Hash, MessageSquare, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  useChatSearch,
  useFollowedThreads,
  useSavedMessages,
  type ChatMessageView,
  type ChatSearchHit,
} from './api';

export type ChatViewKind = 'threads' | 'saved' | 'search';

/** Where a row wants to take you: a conversation, and possibly a thread in it. */
export interface JumpTarget {
  conversationId: string;
  messageId?: string;
  threadRootId?: string;
}

function EmptyState({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 p-8 text-center" data-testid="chat-view-empty">
      <span className="vt-muted">{icon}</span>
      <p className="max-w-[240px] text-[12px] vt-muted">{children}</p>
    </div>
  );
}

function RowShell({
  onClick,
  testId,
  children,
  ...rest
}: {
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
} & Record<string, unknown>) {
  return (
    <button
      type="button"
      data-testid={testId}
      className="w-full border-b border-[var(--border)] px-3 py-2 text-left last:border-0 hover:bg-[var(--surface-2)]"
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A conversation's name as a person reads it — "#product" or "Ada Lovelace". */
function roomLabel(conversation: { name?: string; type: string }): string {
  if (conversation.type === 'channel') return `#${conversation.name ?? 'channel'}`;
  return conversation.name ?? 'Direct message';
}

/**
 * The matched words, marked — §6.
 *
 * Done in the client rather than server-side `$meta: 'textScore'` highlighting,
 * which MongoDB does not offer: the query terms are already here, and a result
 * whose matched word is not visible is one somebody has to open to evaluate.
 *
 * Terms are escaped before they become a pattern. A search for `a.*b` is a
 * search for that text, not a wildcard — and not a way to hang the render.
 */
export function highlight(text: string, query: string): React.ReactNode[] {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter((term) => term.length >= 2)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (terms.length === 0) return [text];

  const pattern = new RegExp(`(${terms.join('|')})`, 'ig');
  return text.split(pattern).map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} className="bg-[var(--accent)]/25 text-inherit" data-testid="search-match">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

function preview(message: ChatMessageView): string {
  if (message.body.trim()) return message.body;
  if (message.taskRef?.taskKey) return `${message.taskRef.taskKey} · ${message.taskRef.titleSnapshot}`;
  if ((message.attachments ?? []).length > 0) return 'Attachment';
  return '—';
}

/* ── §2 — every thread I follow ─────────────────────────── */

export function ThreadsView({ onJump }: { onJump: (target: JumpTarget) => void }) {
  const threads = useFollowedThreads();

  if (threads.isLoading) return <p className="p-3 text-[12px] vt-muted">Loading…</p>;
  if ((threads.data?.length ?? 0) === 0) {
    return (
      <EmptyState icon={<MessageSquare className="size-6" />}>
        Threads you start, reply to, or are mentioned in show up here. Reply in a thread to follow
        it.
      </EmptyState>
    );
  }

  return (
    <div data-testid="threads-view">
      {threads.data!.map((thread) => (
        <RowShell
          key={thread.root._id}
          testId="thread-row"
          data-conversation-id={thread.conversation._id}
          onClick={() =>
            onJump({
              conversationId: thread.conversation._id,
              threadRootId: thread.root._id,
            })
          }
        >
          <p className="flex items-center gap-1 text-[11px] font-medium text-[var(--accent)]">
            <Hash className="size-3" />
            {roomLabel(thread.conversation)}
          </p>
          <p className="truncate text-[13px]">{preview(thread.root)}</p>
          <p className="flex items-center gap-1 text-[11px] vt-muted" data-testid="thread-row-count">
            {thread.replyCount} {thread.replyCount === 1 ? 'reply' : 'replies'}
            {thread.hasNewReplies && (
              <span
                className="size-1.5 rounded-full bg-[var(--accent)]"
                data-testid="thread-row-new"
                aria-label="New replies"
              />
            )}
          </p>
        </RowShell>
      ))}
    </div>
  );
}

/* ── §5 — my saved messages ─────────────────────────────── */

export function SavedView({ onJump }: { onJump: (target: JumpTarget) => void }) {
  const saved = useSavedMessages();

  if (saved.isLoading) return <p className="p-3 text-[12px] vt-muted">Loading…</p>;
  if ((saved.data?.length ?? 0) === 0) {
    return (
      <EmptyState icon={<Bookmark className="size-6" />}>
        Save a message from its hover menu to keep it here. Only you can see your saved messages.
      </EmptyState>
    );
  }

  return (
    <div data-testid="saved-view">
      {saved.data!.map((row) => (
        <RowShell
          key={row.message._id}
          testId="saved-row"
          data-conversation-id={row.conversation._id}
          onClick={() =>
            onJump({
              conversationId: row.conversation._id,
              messageId: row.message._id,
              threadRootId: row.message.threadRootId,
            })
          }
        >
          <p className="text-[11px] font-medium text-[var(--accent)]">
            {roomLabel(row.conversation)}
          </p>
          <p className="truncate text-[13px]">{preview(row.message)}</p>
        </RowShell>
      ))}
    </div>
  );
}

/* ── §6 — search ────────────────────────────────────────── */

/** Hits in the order they arrived, gathered under the room they came from. */
function groupByConversation(
  hits: ChatSearchHit[],
): { conversation: ChatSearchHit['conversation']; hits: ChatSearchHit[] }[] {
  const groups = new Map<string, { conversation: ChatSearchHit['conversation']; hits: ChatSearchHit[] }>();
  for (const hit of hits) {
    const key = hit.conversation._id;
    // Insertion order is relevance order, so the best-ranked room comes first.
    if (!groups.has(key)) groups.set(key, { conversation: hit.conversation, hits: [] });
    groups.get(key)!.hits.push(hit);
  }
  return [...groups.values()];
}

export function SearchView({
  query,
  onQueryChange,
  conversationId,
  conversationLabel,
  onClearScope,
  onJump,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  /** Set to search inside one room rather than everywhere. */
  conversationId?: string;
  conversationLabel?: string;
  onClearScope?: () => void;
  onJump: (target: JumpTarget) => void;
}) {
  const results = useChatSearch(query, { conversationId });
  const tooShort = query.trim().length < 2;

  return (
    <div data-testid="search-view" data-scoped={conversationId ? 'true' : 'false'}>
      {/* §6 — when search was opened from a conversation's header, say so and
          offer the way out. A narrowed search that does not look narrowed is
          how somebody concludes a message was deleted. */}
      {conversationId && (
        <button
          type="button"
          data-testid="search-scope"
          className="flex w-full items-center gap-1 px-3 pt-2 text-left text-[11px] text-[var(--accent)]"
          onClick={onClearScope}
        >
          In {conversationLabel ? `#${conversationLabel}` : 'this conversation'} · search everywhere
        </button>
      )}
      <div className="p-2">
        <Input
          autoFocus
          data-testid="chat-search-input"
          placeholder={conversationId ? 'Search this conversation' : 'Search messages'}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </div>

      {tooShort && (
        <EmptyState icon={<Search className="size-6" />}>
          Type at least two characters. You can only find messages in conversations you are part
          of.
        </EmptyState>
      )}

      {!tooShort && results.isLoading && <p className="p-3 text-[12px] vt-muted">Searching…</p>}

      {!tooShort && !results.isLoading && (results.data?.length ?? 0) === 0 && (
        <EmptyState icon={<Search className="size-6" />}>
          Nothing matches “{query.trim()}” in your conversations.
        </EmptyState>
      )}

      {/* §6 — grouped by conversation. Ten hits in one channel read as one
          answer; the same ten interleaved with two others read as noise. */}
      {groupByConversation(results.data ?? []).map((group) => (
        <div key={group.conversation._id} data-testid="search-group">
          <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide vt-muted">
            {roomLabel(group.conversation)}
          </p>
          {group.hits.map((hit) => (
            <RowShell
              key={hit.message._id}
              testId="search-result"
              data-conversation-id={hit.conversation._id}
              onClick={() =>
                onJump({
                  conversationId: hit.conversation._id,
                  messageId: hit.message._id,
                  // §2 — a hit inside a thread opens the thread, because the
                  // main scroll is deliberately not where that message lives.
                  threadRootId: hit.threadRootId ?? undefined,
                })
              }
            >
              <p className="truncate text-[13px]">{highlight(preview(hit.message), query)}</p>
            </RowShell>
          ))}
        </div>
      ))}
    </div>
  );
}
